import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Select, Space, Statistic, Typography } from 'antd';
import { LineChartOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '@/hooks/useProjects';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Title, Text } = Typography;

const ProjectHealthHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { data: projects = [], isLoading } = useProjects();
  const [selectedId, setSelectedId] = useState<string | undefined>();

  const options = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: `${p.project_number || p.id} — ${p.project_name || ''}`,
      })),
    [projects]
  );

  const overrunProjects = useMemo(() => {
    return (projects as any[]).filter((p) => {
      const lf = p.list_financials;
      if (!lf) return false;
      return Boolean(lf.any_bva_over) || (Number(lf.billed_profit) < 0 && Number(lf.invoiced_total) > 0);
    });
  }, [projects]);

  const breadcrumbSegments = useMemo(
    () => [
      { title: 'Projects', path: '/prj/projects' },
      { title: 'Project health hub' },
    ],
    []
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <Title level={4}>
        <LineChartOutlined /> Project health hub
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Choose a project to open its 360-degree health check. Portfolio flags below use Cost Control
        overrun and billed-margin signals (same source as Projects list).
      </Text>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Projects" value={projects.length} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Overrun / margin risk"
              value={overrunProjects.length}
              valueStyle={{ color: overrunProjects.length ? '#cf1322' : undefined }}
              prefix={overrunProjects.length ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Negative billed P/L"
              value={(projects as any[]).filter(
                (p) =>
                  Number(p.list_financials?.billed_profit) < 0 &&
                  Number(p.list_financials?.invoiced_total) > 0
              ).length}
            />
          </Card>
        </Col>
      </Row>

      {overrunProjects.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Projects with BVA overrun or negative billed margin"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {overrunProjects.slice(0, 8).map((p: any) => (
                <li key={p.id}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/prj/projects/${p.id}/status-check`)}
                  >
                    {p.project_number} — {p.project_name}
                  </Button>
                  {p.list_financials?.any_bva_over ? ' · BVA over' : ''}
                  {Number(p.list_financials?.billed_profit) < 0
                    ? ` · billed ${formatCurrency(Number(p.list_financials.billed_profit))}`
                    : ''}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Select
            showSearch
            allowClear
            placeholder="Search by project number or name"
            style={{ width: '100%' }}
            loading={isLoading}
            options={options}
            optionFilterProp="label"
            value={selectedId}
            onChange={setSelectedId}
          />
          <Button
            type="primary"
            disabled={!selectedId}
            icon={<LineChartOutlined />}
            onClick={() => selectedId && navigate(`/prj/projects/${selectedId}/status-check`)}
          >
            Open health check
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default ProjectHealthHubPage;
